import {
    createElement,
    forwardRef,
    useEffect,
    useRef,
} from 'react';

import classNames from 'classnames';
import mergeRefs from 'merge-refs';

import type {
    CSSProperties,
    ComponentPropsWithRef,
    ElementType,
    ReactNode,
} from 'react';
import type React from 'react';


export type MultiLineEllipsisProps<T extends ElementType = 'div'> = {
    /**
     * Content to truncate. Should be text or inline JSX whose rendered output
     * can be meaningfully clamped to the specified number of lines.
     */
    children: ReactNode,

    /**
     * HTML element type to render as the container. Accepts any valid element
     * type string (e.g. `"p"`, `"span"`, `"li"`). Defaults to `"div"`.
     */
    as?: T,

    /**
     * Maximum number of visible lines before the content is truncated with an
     * ellipsis. Passed as the `--clamp-var` CSS custom property and consumed by
     * the `multiline-ellipsis` global CSS class via `-webkit-line-clamp`. Defaults
     * to `1`.
     */
    lines?: number,
} & ComponentPropsWithRef<T>;

/**
 * MultiLineEllipsis Component Interface
 * @interface MultiLineEllipsisComponent
 */
interface MultiLineEllipsisComponent {
    /**
     * MultiLineEllipsis Component Function
     */
    <T extends ElementType = 'div'>(
        props: MultiLineEllipsisProps<T> & {ref?: React.Ref<HTMLElement>}
    ): React.ReactElement,

    /**
     * MultiLineEllipsis Component Display Name
     */
    displayName: string,
}

/**
 * Polymorphic text truncation component that clamps content to a specified
 * number of lines using CSS `-webkit-line-clamp` and shows a native tooltip
 * when the content is actually clipped.
 *
 * Applies the global `multiline-ellipsis` CSS class (which sets up the clamp
 * via the `--clamp-var` custom property) and uses a `ResizeObserver` to
 * dynamically add or remove the `title` attribute as the container resizes.
 * Supports any HTML element via the `as` prop.
 *
 * As an alternative to this component you can apply the global CSS class
 * directly — see the
 * [Global Styles docs]{@link https://storybook.dev.pitchbookdata.com/iframe.html?viewMode=docs&id=core-global-styles-other-classes--documentation&globals=#multiline-ellipsis-class}.
 *
 * @see [Storybook]{@link https://storybook.dev.pitchbookdata.com/?path=/docs/text-multiline-ellipsis--documentation}
 */
export const MultiLineEllipsis = forwardRef(
    <T extends ElementType = 'div'>(
        {
            as = 'div' as T,
            children,
            lines = 1,
            title,
            ...otherProps
        }: Omit<MultiLineEllipsisProps<T>, 'ref'>,
        ref: React.Ref<HTMLElement>,
    ): React.ReactElement => {
        const containerRef = useRef<HTMLElement | null>(null);

        useEffect(() => {
            const currentContainer = containerRef.current;

            if (!currentContainer || !title) {
                return;
            }
            const observer = new ResizeObserver(() => {
                const isEllipsisActive = currentContainer.offsetWidth < currentContainer.scrollWidth
                    || currentContainer.offsetHeight < currentContainer.scrollHeight;
                if (isEllipsisActive && title) {
                    currentContainer.setAttribute('title', title);
                } else {
                    currentContainer.removeAttribute('title');
                }
            });

            if (currentContainer) {
                observer.observe(currentContainer);
            }

            return () => {
                observer.disconnect();
            };
        }, [title]);

        const multilineEllipsisClass = classNames('multiline-ellipsis', otherProps.className);

        const multilineEllipsisStyle = {
            '--clamp-var': lines,
            ...otherProps.style,
        } as CSSProperties;

        return createElement(as, {
            ...otherProps,
            ref: mergeRefs<HTMLElement>(ref, containerRef),
            className: multilineEllipsisClass,
            style: multilineEllipsisStyle,
        }, children);
    }) as MultiLineEllipsisComponent;

MultiLineEllipsis.displayName = 'MultiLineEllipsis';
